import React, { useEffect, useState, useMemo } from 'react';
import { Form, Typography, Button, Card, Space, message } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, GlobalOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedGoHighLevel = ({ destinations, activeDestination, btnClassName }) => {
    const [customers, setCustomers] = useState([
        { id: Date.now(), customerEmail: "", timezoneSelected: "", goHighLevelApiKey: "", sendLeadsLimitGoHighLevel: "" }
    ]);

    const DESTINATION_GOHIGHLEVEL = SOURCES.DESTINATION_GOHIGHLEVEL || "gohighlevel";

    const { data: timezones, isLoading: isTimezonesLoading } = useGenericQuery({
        endpoint: "common/timezones",
        queryKey: ["common", "timezones"],
    });

    const timezoneOptions = useMemo(() => {
        if (!timezones) return [];
        return timezones.map((tz) => {
            const formattedOffset = `UTC${tz.utc[0] >= 0 ? "+" : ""}${tz.utc[0]}`;
            return {
                label: `${tz.text} ${formattedOffset}`,
                value: tz.utc[0],
            };
        });
    }, [timezones]);

    const { mutate: saveGoHighLevel, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => message.success("GoHighLevel configuration saved successfully")
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_GOHIGHLEVEL);
            if (config) {
                if (config.extraInfo && config.extraInfo.length > 0) {
                    setCustomers(config.extraInfo.map((info, idx) => ({
                        id: Date.now() + idx,
                        customerEmail: info.customerEmail || "",
                        timezoneSelected: info.timezoneSelected || "",
                        goHighLevelApiKey: info.secretKey || config.secretKey,
                        sendLeadsLimitGoHighLevel: info.sendLeadsLimit || config.leadSentConfig?.sentLimit,
                    })));
                } else {
                    setCustomers([{
                        id: Date.now(),
                        customerEmail: "",
                        timezoneSelected: "",
                        goHighLevelApiKey: config.secretKey || "",
                        sendLeadsLimitGoHighLevel: config.leadSentConfig?.sentLimit || 0,
                    }]);
                }
            }
        }
    }, [destinations]);

    const handleAddCustomer = () => {
        setCustomers(prev => [
            ...prev,
            { id: Date.now(), customerEmail: "", timezoneSelected: "", goHighLevelApiKey: "", sendLeadsLimitGoHighLevel: "" }
        ]);
    };

    const handleRemoveCustomer = (id) => {
        if (customers.length === 1) return;
        setCustomers(prev => prev.filter(c => c.id !== id));
    };

    const handleFieldChange = (id, field, value) => {
        setCustomers(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const onFinish = () => {
        if (customers.some(c => !c.goHighLevelApiKey)) {
            message.warning("API Key is required for all customers");
            return;
        }

        if (customers.length > 1) {
            // Multiple customers: Send extraInfo
            const extraInfo = customers.map(c => ({
                secretKey: c.goHighLevelApiKey,
                sendLeadsLimit: c.sendLeadsLimitGoHighLevel,
                customerEmail: c.customerEmail,
                timezoneSelected: c.timezoneSelected,
            }));

            saveGoHighLevel({
                extraInfo,
                integrationCompany: DESTINATION_GOHIGHLEVEL,
            });
        } else {
            // Single customer: Send individual data
            const customer = customers[0];
            saveGoHighLevel({
                secretKey: customer.goHighLevelApiKey,
                sendLeadsLimit: customer.sendLeadsLimitGoHighLevel,
                integrationCompany: DESTINATION_GOHIGHLEVEL,
            });
        }
    };

    const getFormData = (index, customer) => {
        const data = [];

        if (index !== 0) {
            data.push(
                {
                    inputType: SOURCES.FORMINPUTTYPES.INPUT,
                    label: "Customer Email",
                    name: "customerEmail",
                    placeholder: "Enter customer email",
                    type: "email",
                    required: true,
                    span: 12,
                    initialValue: customer.customerEmail
                },
                {
                    inputType: SOURCES.FORMINPUTTYPES.SELECT,
                    label: "Customer Timezone",
                    name: "timezoneSelected",
                    placeholder: "Select timezone",
                    options: timezoneOptions,
                    loading: isTimezonesLoading,
                    required: true,
                    span: 12,
                    initialValue: customer.timezoneSelected
                }
            );
        }

        data.push(
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                label: "API Key",
                name: "goHighLevelApiKey",
                placeholder: "Get API key from GoHighLevel Application",
                required: true,
                span: 12,
                initialValue: customer.goHighLevelApiKey
            },
            {
                inputType: SOURCES.FORMINPUTTYPES.INPUT,
                label: "How Many Leads Do You Want To Send Initially?",
                name: "sendLeadsLimitGoHighLevel",
                placeholder: "Total lead(s) send initially",
                type: "text",
                span: 12,
                initialValue: customer.sendLeadsLimitGoHighLevel
            }
        );

        return data;
    };

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white  flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage multiple GoHighLevel customer accounts and lead sync.</Text>
                </div>
            </div>

            <div className="space-y-6">
                <div className="bg-primary/10 p-3 rounded-xl">
                    <div className="flex items-center gap-3">
                        <GlobalOutlined className="text-primary mt-0" />
                        <Text className="text-sm text-primary">
                            <Text strong className="text-primary">NOTE:</Text> You can get your API key from Setting → Business Profile in your GoHighLevel dashboard.
                        </Text>
                    </div>
                </div>

                {customers.map((customer, index) => (
                    <Card
                        key={customer.id}
                        size="small"
                        className="rounded-2xl border-gray-100 shadow-sm overflow-hidden"
                        title={
                            <div className="flex justify-between items-center py-1">
                                <Space>
                                    <div className="size-8 rounded-lg bg-gray-50 flex items-center justify-center text-xs font-bold text-gray-400">
                                        {index + 1}
                                    </div>
                                    <Text strong>{index === 0 ? "Default Customer" : `Customer: ${customer.customerEmail || "New Account"}`}</Text>
                                </Space>
                                {index > 0 && (
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleRemoveCustomer(customer.id)}
                                        className="hover:bg-red-50"
                                    />
                                )}
                            </div>
                        }
                    >
                        <CommonForm
                            data={getFormData(index, customer)}
                            onValuesChange={(changed) => {
                                const field = Object.keys(changed)[0];
                                handleFieldChange(customer.id, field, changed[field]);
                            }}
                            layoutType="vertical"
                            className="p-2"
                        />
                    </Card>
                ))}

                <div className="flex justify-between items-center pt-4">
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddCustomer}
                        className="h-10 rounded-xl border-dashed hover:border-primary hover:text-primary"
                    >
                        Customer
                    </Button>

                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={onFinish}
                        className={cn(btnClassName, "rounded-xl shadow-md")}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EnhancedGoHighLevel;
